import React, { useEffect, useRef } from 'react'

/**
 * Live ownership-network hero: nodes (pills = persons, squares =
 * companies, the odd red one = a flag) linked when close, with signals
 * running along the links.
 *
 * Every node hangs on a soft spring from a home point that slowly wanders,
 * so the network breathes on its own. The cursor tugs gently at the nodes
 * near it — a slight pull towards the pointer plus a little of its
 * movement, like a hand through water — and the springs ease them back
 * when it leaves. A click sends a burst of signals from the closest node.
 *
 * Decorative only (`aria-hidden`). Pauses off-screen and draws a single
 * still frame when the user prefers reduced motion.
 */
interface LiveNetworkProps {
  className?: string
}

type Kind = 'person' | 'company' | 'flag'

interface NodeState {
  x: number
  y: number
  vx: number
  vy: number
  /** Home point as a fraction of the canvas, so resizes keep the layout. */
  hx: number
  hy: number
  /** Phases and speeds of the slow wander around home. */
  px: number
  py: number
  wx: number
  wy: number
  r: number
  kind: Kind
  /** 0–1 glow when the cursor is near */
  heat: number
}

interface Pulse {
  from: number
  to: number
  t: number
  speed: number
  flag: boolean
}

const INK = '18, 21, 26'
const FLAG = '201, 58, 34'
const LINK_DIST = 135
/** How far the cursor reaches into the network. */
const REACH = 190
/** Spring back to home, and how much speed survives each frame. */
const SPRING = 0.011
const DAMPING = 0.9
/** Pull towards the cursor at point-blank range; settles nodes ~25px over. */
const PULL = 0.28
/** Share of the cursor's own movement handed to nodes right under it. */
const DRAG = 0.045
/** Radius of the slow wander around home, in px. */
const WANDER = 12

function randomKind(): Kind {
  const roll = Math.random()
  if (roll < 0.07) return 'flag'
  return roll < 0.55 ? 'company' : 'person'
}

const LiveNetwork: React.FC<LiveNetworkProps> = ({ className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    let width = 0
    let height = 0
    let nodes: NodeState[] = []
    let pulses: Pulse[] = []
    // Raw pointer position, and a smoothed copy the physics follows
    const pointer = { x: 0, y: 0, inside: false }
    const cursor = { x: 0, y: 0, vx: 0, vy: 0, strength: 0 }
    let frame = 0
    let visible = true
    let lastPulse = 0
    let lastTime = 0

    const homeX = (n: NodeState, t: number) => n.hx * width + Math.sin(t * n.wx + n.px) * WANDER
    const homeY = (n: NodeState, t: number) => n.hy * height + Math.cos(t * n.wy + n.py) * WANDER

    const seed = () => {
      const count = Math.max(22, Math.min(90, Math.round((width * height) / 5200)))
      // Jittered grid: even coverage without the clumps of pure random.
      const cols = Math.max(1, Math.round(Math.sqrt((count * width) / Math.max(height, 1))))
      const rows = Math.max(1, Math.ceil(count / cols))
      nodes = Array.from({ length: cols * rows }, (_, i) => {
        const kind = randomKind()
        const hx = ((i % cols) + 0.15 + Math.random() * 0.7) / cols
        const hy = (Math.floor(i / cols) + 0.15 + Math.random() * 0.7) / rows
        return {
          x: hx * width,
          y: hy * height,
          vx: 0,
          vy: 0,
          hx,
          hy,
          px: Math.random() * Math.PI * 2,
          py: Math.random() * Math.PI * 2,
          wx: 0.00018 + Math.random() * 0.00022,
          wy: 0.00018 + Math.random() * 0.00022,
          r: kind === 'company' ? 3 + Math.random() * 1.5 : 2.4 + Math.random() * 1.6,
          kind,
          heat: 0,
        }
      })
      pulses = []
    }

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const first = width === 0
      width = rect.width
      height = rect.height
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      if (first || nodes.length === 0) seed()
      if (reduceMotion) {
        nodes.forEach((n) => {
          n.x = homeX(n, 0)
          n.y = homeY(n, 0)
        })
        draw()
      }
    }

    const nearestNode = (x: number, y: number): number => {
      let best = -1
      let bestD = Infinity
      nodes.forEach((n, i) => {
        const d = (n.x - x) ** 2 + (n.y - y) ** 2
        if (d < bestD) {
          bestD = d
          best = i
        }
      })
      return best
    }

    const spawnPulse = (from: number) => {
      const a = nodes[from]
      const targets: number[] = []
      nodes.forEach((b, j) => {
        if (j !== from && Math.hypot(a.x - b.x, a.y - b.y) < LINK_DIST) targets.push(j)
      })
      if (targets.length === 0) return
      const to = targets[Math.floor(Math.random() * targets.length)]
      pulses.push({
        from,
        to,
        t: 0,
        speed: 0.012 + Math.random() * 0.01,
        flag: a.kind === 'flag' || nodes[to].kind === 'flag',
      })
    }

    const drawNode = (n: NodeState) => {
      const glow = n.heat
      if (n.kind === 'flag') {
        ctx.fillStyle = `rgba(${FLAG}, ${0.75 + glow * 0.25})`
      } else {
        ctx.fillStyle = `rgba(${INK}, ${0.38 + glow * 0.4})`
      }
      const r = n.r + glow * 0.9
      ctx.beginPath()
      if (n.kind === 'company') {
        const s = r * 1.7
        ctx.roundRect(n.x - s / 2, n.y - s / 2, s, s, 1.5)
      } else {
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2)
      }
      ctx.fill()
    }

    function draw() {
      ctx!.clearRect(0, 0, width, height)

      // Links between nearby nodes
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i]
          const b = nodes[j]
          const d = Math.hypot(a.x - b.x, a.y - b.y)
          if (d > LINK_DIST) continue
          const heat = Math.max(a.heat, b.heat)
          const alpha = (1 - d / LINK_DIST) * (0.22 + heat * 0.3)
          ctx!.strokeStyle = `rgba(${INK}, ${alpha})`
          ctx!.lineWidth = 1
          ctx!.beginPath()
          ctx!.moveTo(a.x, a.y)
          ctx!.lineTo(b.x, b.y)
          ctx!.stroke()
        }
      }

      // Signals along links
      pulses.forEach((p) => {
        const a = nodes[p.from]
        const b = nodes[p.to]
        const x = a.x + (b.x - a.x) * p.t
        const y = a.y + (b.y - a.y) * p.t
        ctx!.fillStyle = p.flag ? `rgba(${FLAG}, 0.95)` : `rgba(${INK}, 0.85)`
        ctx!.beginPath()
        ctx!.arc(x, y, 2.1, 0, Math.PI * 2)
        ctx!.fill()
      })

      nodes.forEach(drawNode)
    }

    const step = (now: number) => {
      frame = requestAnimationFrame(step)
      if (!visible) {
        lastTime = now
        return
      }
      // Frame-rate independent: 1 = one 60 Hz frame
      const dt = lastTime ? Math.min(3, (now - lastTime) / 16.67) : 1
      lastTime = now

      // The physics cursor trails the pointer a little, which keeps the
      // pull soft, and fades in and out instead of switching on and off.
      const tx = pointer.inside ? pointer.x : cursor.x
      const ty = pointer.inside ? pointer.y : cursor.y
      const ease = 1 - Math.pow(0.82, dt)
      const nx = cursor.x + (tx - cursor.x) * ease
      const ny = cursor.y + (ty - cursor.y) * ease
      cursor.vx = (nx - cursor.x) / dt
      cursor.vy = (ny - cursor.y) / dt
      cursor.x = nx
      cursor.y = ny
      cursor.strength += ((pointer.inside ? 1 : 0) - cursor.strength) * (1 - Math.pow(0.94, dt))

      const keep = Math.pow(DAMPING, dt)
      nodes.forEach((n) => {
        let ax = (homeX(n, now) - n.x) * SPRING
        let ay = (homeY(n, now) - n.y) * SPRING
        let near = 0

        if (cursor.strength > 0.01) {
          const dx = cursor.x - n.x
          const dy = cursor.y - n.y
          const d = Math.hypot(dx, dy)
          if (d < REACH) {
            near = (1 - d / REACH) ** 2 * cursor.strength
            if (d > 1) {
              ax += (dx / d) * PULL * near
              ay += (dy / d) * PULL * near
            }
            ax += cursor.vx * DRAG * near
            ay += cursor.vy * DRAG * near
          }
        }

        n.vx = (n.vx + ax * dt) * keep
        n.vy = (n.vy + ay * dt) * keep
        n.x += n.vx * dt
        n.y += n.vy * dt
        n.heat += (Math.min(1, near * 2.2) - n.heat) * (1 - Math.pow(0.9, dt))
      })

      if (now - lastPulse > 480 && nodes.length > 0) {
        lastPulse = now
        const from = cursor.strength > 0.5 && Math.random() < 0.5
          ? nearestNode(cursor.x, cursor.y)
          : Math.floor(Math.random() * nodes.length)
        spawnPulse(from)
      }
      pulses.forEach((p) => (p.t += p.speed * dt))
      pulses = pulses.filter((p) => p.t < 1)

      draw()
    }

    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      // The pull starts a little before the pointer reaches the canvas.
      const margin = REACH * 0.5
      const inside = x >= -margin && y >= -margin && x <= width + margin && y <= height + margin
      if (inside && !pointer.inside && cursor.strength < 0.05) {
        // Start the physics cursor where the pointer enters, not where it left.
        cursor.x = x
        cursor.y = y
      }
      pointer.x = x
      pointer.y = y
      pointer.inside = inside
    }
    const onLeave = () => {
      pointer.inside = false
    }
    const onClick = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      const from = nearestNode(e.clientX - rect.left, e.clientY - rect.top)
      if (from < 0) return
      for (let k = 0; k < 6; k++) spawnPulse(from)
      nodes[from].heat = 1
    }

    const ro = new ResizeObserver(resize)
    ro.observe(canvas)
    resize()

    let io: IntersectionObserver | null = null
    if (!reduceMotion) {
      io = new IntersectionObserver(([entry]) => {
        visible = entry?.isIntersecting ?? true
      })
      io.observe(canvas)
      // Listen on the window so the network reacts even when the pointer
      // is over the text next to it.
      window.addEventListener('pointermove', onMove)
      document.addEventListener('pointerleave', onLeave)
      canvas.addEventListener('pointerdown', onClick)
      frame = requestAnimationFrame(step)
    }

    return () => {
      cancelAnimationFrame(frame)
      ro.disconnect()
      io?.disconnect()
      window.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerleave', onLeave)
      canvas.removeEventListener('pointerdown', onClick)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={`block h-full w-full [mask-image:radial-gradient(ellipse_at_center,black_55%,transparent_100%)] ${className}`}
    />
  )
}

export default LiveNetwork
